import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { useWritableMinistries } from '../hooks/useProjectCatalogs';
import { buttonVariants } from '@/shared/components/ui/button';
import { messages } from '@/shared/constants/messages';
import type { Id } from '@/shared/types/domain';

interface NewProjectButtonProps {
  /** Quando a tela já tem um ministério em foco, ele vai pré-selecionado. */
  ministryId?: Id;
  variant?: 'default' | 'outline';
}

/**
 * "Novo projeto" — o ponto de entrada do formulário, na tela de Projetos e na
 * de Ministério.
 *
 * **Navega, nunca abre diálogo** (decisão 21): o formulário é um documento de
 * cinco seções com autosave e alerta ao vivo, e isso não cabe em meia tela
 * sobre um fundo escurecido. Um modal fechado por engano no meio da escrita é
 * a pior sensação que este formulário pode produzir, mesmo com o rascunho
 * salvo.
 *
 * O botão SOME para quem não pode escrever em nenhum ministério — e some
 * porque a RPC devolveu lista vazia, não porque o front decidiu: é a mesma
 * fonte que autoriza a escrita de verdade.
 */
export function NewProjectButton({ ministryId, variant = 'default' }: NewProjectButtonProps) {
  const { data: ministries } = useWritableMinistries();
  if (!ministries || ministries.length === 0) return null;

  // Ministério que a pessoa não pode escrever não vira pré-seleção: a tela do
  // ministério existe para todo mundo que o enxerga, e só parte deles escreve.
  const canWriteHere = ministryId
    ? ministries.some((ministry) => ministry.ministry_id === ministryId)
    : true;
  if (ministryId && !canWriteHere) return null;

  // O formulário mora sob `/calendario` (ver `LegacyFormRedirect` em
  // `router.tsx`): é a aba que os cinco papéis têm, e é de onde este botão é
  // clicado na maioria das vezes.
  const to = ministryId ? `/calendario/novo?ministerio=${ministryId}` : '/calendario/novo';

  return (
    <Link to={to} className={buttonVariants({ variant })}>
      <Plus aria-hidden="true" className="size-4" />
      {messages.form.newProject}
    </Link>
  );
}
